import type { FormEvent, ReactElement } from 'react';
import {
  contractStatusClass,
  formatContractStatus,
  formatContractType,
  type ContractListRow,
  type ContractStatusFilter
} from '../../domain/contracts';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from './GridPaginationBar';

const CONTRACT_COLUMNS: GroupedColumnDef[] = [
  { id: 'number', label: 'Number', header: 'Number' },
  { id: 'type', label: 'Type', header: 'Type' },
  { id: 'title', label: 'Title', header: 'Title' },
  { id: 'status', label: 'Status', header: 'Status' },
  { id: 'region', label: 'Region', header: 'Region' },
  { id: 'parties', label: 'Parties', header: 'Parties' },
  { id: 'wells', label: 'Wells', header: 'Wells' }
];

interface ContractsGridProps {
  rows: ContractListRow[];
  selectedId: string | null;
  onSelectContract: (id: string) => void;
  onOpenContractInPage?: (id: string) => void;
  statusFilter: ContractStatusFilter;
  onStatusFilterChange: (filter: ContractStatusFilter) => void;
  numberFilter: string;
  partyFilter: string;
  wellFilter: string;
  onNumberFilterChange: (value: string) => void;
  onPartyFilterChange: (value: string) => void;
  onWellFilterChange: (value: string) => void;
  onSearch: () => void;
  loading?: boolean;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onNewContract?: () => void;
}

function getGroupValue(row: ContractListRow, columnId: string): string {
  switch (columnId) {
    case 'number':
      return row.number || '—';
    case 'type':
      return formatContractType(row.type ?? '');
    case 'title':
      return row.title || '—';
    case 'status':
      return formatContractStatus(row.status);
    case 'region':
      return row.operatingRegion || '—';
    case 'parties':
      return row.partyNames?.trim() || '—';
    case 'wells':
      return row.wellApis?.trim() || '—';
    default:
      return '—';
  }
}

export function ContractsGrid({
  rows,
  selectedId,
  onSelectContract,
  onOpenContractInPage,
  statusFilter,
  onStatusFilterChange,
  numberFilter,
  partyFilter,
  wellFilter,
  onNumberFilterChange,
  onPartyFilterChange,
  onWellFilterChange,
  onSearch,
  loading,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  onNewContract
}: ContractsGridProps): ReactElement {
  function renderRow(row: ContractListRow): ReactElement {
    return (
      <tr
        className={selectedId === row.id ? 'selected' : undefined}
        onClick={() => onSelectContract(row.id)}
        onDoubleClick={(e) => {
          e.preventDefault();
          onOpenContractInPage?.(row.id);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectContract(row.id);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <td className="mono-id">{row.number}</td>
        <td>{formatContractType(row.type ?? '')}</td>
        <td>{row.title}</td>
        <td>
          <span className={contractStatusClass(row.status)}>{formatContractStatus(row.status)}</span>
        </td>
        <td>{row.operatingRegion}</td>
        <td>{row.partyNames?.trim() || '—'}</td>
        <td>{row.wellApis?.trim() || '—'}</td>
      </tr>
    );
  }

  function onFilterSubmit(e: FormEvent): void {
    e.preventDefault();
    onSearch();
  }

  return (
    <section className="card requests-grid-card" data-testid="contracts-grid">
      <div className="row spread requests-grid-toolbar">
        <h2>Contracts</h2>
        {onNewContract && (
          <button
            type="button"
            className="secondary well-add-btn"
            title="New contract"
            aria-label="New contract"
            onClick={(e) => {
              e.stopPropagation();
              onNewContract();
            }}
          >
            +
          </button>
        )}
      </div>
      <p className="hint">Click a row for the side panel; double-click to open the full page</p>
      <form className="row filter-row" onSubmit={onFilterSubmit}>
        <label htmlFor="contractStatusFilter">Status</label>
        <select
          id="contractStatusFilter"
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value as ContractStatusFilter)}
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="terminated">Terminated</option>
        </select>
        <label htmlFor="contracts-filter-number">Number</label>
        <input
          id="contracts-filter-number"
          data-testid="contracts-filter-number"
          placeholder="Contract number"
          value={numberFilter}
          onChange={(e) => onNumberFilterChange(e.target.value)}
        />
        <label htmlFor="contracts-filter-party">Party</label>
        <input
          id="contracts-filter-party"
          data-testid="contracts-filter-party"
          placeholder="Party name"
          value={partyFilter}
          onChange={(e) => onPartyFilterChange(e.target.value)}
        />
        <label htmlFor="contracts-filter-well">Well</label>
        <input
          id="contracts-filter-well"
          data-testid="contracts-filter-well"
          placeholder="Well API or name"
          value={wellFilter}
          onChange={(e) => onWellFilterChange(e.target.value)}
        />
        <button type="submit" data-testid="contracts-search">
          Search
        </button>
      </form>
      {loading && <p className="hint">Loading contracts…</p>}
      <GroupedDataGrid<ContractListRow>
        columns={CONTRACT_COLUMNS}
        rows={rows}
        getRowId={(r) => r.id}
        getGroupValue={getGroupValue}
        renderRow={renderRow}
        colSpan={7}
        emptyMessage="No contracts match this status."
        groupHint="Click a column title to sort. Drag the grip (⠿) beside a title into the strip above to group. Applies to this page."
      />
      <GridPaginationBar
        idPrefix="contracts-grid"
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </section>
  );
}
